/** @format */

import { v4 as uuidv4 } from 'uuid';
import type { ContentPart, ContentEditorApiDocument, ContentEditorApiPost, ContentEditorApiResponse } from 'src/types';

const DUPLICATE_TITLE_SUFFIX = ' (duplicate)';

function emptyContentPart(): ContentPart {
  return { id: uuidv4(), type: 'content', weight: 0, text: '' };
}

function sanitizeHtmlLikeText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
}

function sortParts<T extends { weight?: number }>(parts: T[]): T[] {
  return [...parts].sort((a, b) => {
    const wa = Number.isFinite(a?.weight) ? (a.weight as number) : 0;
    const wb = Number.isFinite(b?.weight) ? (b.weight as number) : 0;
    return wa - wb;
  });
}

export interface ContentEditorPayload {
  mode: 'create' | 'edit';
  contentId: string | null;
  locale: string;
  title: string;
  parts: ContentPart[];
  topicIds: Array<string | number>;
  selectedTags: Array<{ id: string | number; label: string }>;
  sendNotification: boolean;
  releaseDate: string | null;
}

export function unwrapContentPostPayload(payload: unknown): ContentEditorApiPost | null {
  if (!payload || typeof payload !== 'object') return null;

  const nested = Array.isArray(payload)
    ? payload
    : (payload as ContentEditorApiResponse).contentPost;

  if (Array.isArray(nested)) {
    const first = nested[0];
    return first && typeof first === 'object' ? (first as ContentEditorApiPost) : null;
  }

  if (nested && typeof nested === 'object') {
    return nested as ContentEditorApiPost;
  }

  return payload as ContentEditorApiPost;
}

/**
 * Maps GET .../content-posts/:id/editor (contentPost[0] or an unwrapped post) into contentEditor slice payload.
 * Part shapes align with backend `contentParts` items.
 */
export function mapContentDetailToEditorPayload(
  detail: ContentEditorApiPost | ContentEditorApiResponse | ContentEditorApiPost[] | null | undefined
): ContentEditorPayload | null {
  const unwrapped = unwrapContentPostPayload(detail);
  if (!unwrapped) {
    return null;
  }

  const rawParts = Array.isArray(unwrapped.contentParts) ? unwrapped.contentParts : [];
  const sorted = sortParts(rawParts);
  const parts: ContentPart[] = [];

  for (const part of sorted) {
    const apiType = part?.type;
    if (apiType === 'content') {
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'content',
        weight: part.weight ?? parts.length,
        text: sanitizeHtmlLikeText(part.text)
      });
      continue;
    }

    if (apiType === 'documents') {
      const docs = Array.isArray(part.documents) ? part.documents : [];
      const documents = docs.map(d => {
        const doc = d as ContentEditorApiDocument & { localId?: string; file?: File | null };
        return {
          ...doc,
          localId: doc.localId != null ? String(doc.localId) : doc.id != null ? `id-${doc.id}` : uuidv4(),
          file: doc.file || null,
          remoteUrl: typeof doc.url === 'string' ? doc.url : undefined
        };
      });
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'documents',
        weight: part.weight ?? parts.length,
        documents
      });
      continue;
    }

    if (apiType === 'imageText') {
      const img = part.image && typeof part.image === 'object' ? { ...part.image } : null;
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'imageText',
        weight: part.weight ?? parts.length,
        title: typeof part.title === 'string' ? part.title : '',
        text: sanitizeHtmlLikeText(part.text),
        image: img
      });
      continue;
    }

    if (apiType === 'salesInformationNo') {
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'salesInformationNo',
        weight: part.weight ?? parts.length,
        salesInformationNo: typeof part.salesInformationNo === 'string' ? part.salesInformationNo : ''
      });
      continue;
    }

    if (apiType === 'serviceInformationNo') {
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'serviceInformationNo',
        weight: part.weight ?? parts.length,
        siNumber: typeof part.siNumber === 'string' ? part.siNumber : ''
      });
      continue;
    }

    if (apiType === 'video') {
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'video',
        weight: part.weight ?? parts.length,
        video: part.video === undefined ? null : part.video
      });
      continue;
    }

    if (apiType === 'image') {
      const img = part.image && typeof part.image === 'object' ? { ...part.image } : null;
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'image',
        weight: part.weight ?? parts.length,
        image: img
      });
      continue;
    }

    if (apiType === 'casaNumber') {
      parts.push({
        id: part.id != null ? part.id : uuidv4(),
        type: 'casaNumber',
        weight: part.weight ?? parts.length,
        casaLinkActive: Boolean(part.casaLinkActive),
        casaPartNumbers: Array.isArray(part.casaPartNumbers) ? [...part.casaPartNumbers] : [],
        casaHierarchies: Array.isArray(part.casaHierarchies) ? [...part.casaHierarchies] : []
      });
      continue;
    }
  }

  if (parts.length === 0) {
    parts.push(emptyContentPart());
  }

  parts.forEach((p, i) => {
    p.weight = i;
  });

  const topicIds = Array.isArray(unwrapped.topics)
    ? [
        ...new Map(
          unwrapped.topics
            .map(topic => topic.id)
            .filter(id => id != null)
            .map(id => [String(id), id])
        ).values()
      ]
    : [];
  const selectedTags = Array.isArray(unwrapped.tags)
    ? unwrapped.tags.map(t => ({
        id: t.id,
        label: typeof t.name === 'string' ? t.name : String(t.id)
      }))
    : [];

  return {
    mode: 'edit',
    contentId: unwrapped.id == null || String(unwrapped.id).trim() === '' ? null : String(unwrapped.id),
    locale: typeof unwrapped.locale === 'string' && unwrapped.locale.trim() ? unwrapped.locale.trim() : 'en',
    title: typeof unwrapped.title === 'string' ? unwrapped.title : '',
    parts,
    topicIds,
    selectedTags,
    sendNotification: Boolean(unwrapped.activateNotifications),
    releaseDate: unwrapped.releaseDate ?? null
  };
}

function clonePartForDuplicate(part: ContentPart): ContentPart {
  const id = uuidv4();
  if (part.type === 'documents') {
    return {
      ...part,
      id,
      documents: []
    };
  }
  if (part.type === 'image' || part.type === 'imageText') {
    return {
      ...part,
      id,
      image: part.image && typeof part.image === 'object' ? { ...part.image } : null,
      _file: null,
      _imageRemove: false
    };
  }
  if (part.type === 'video') {
    return {
      ...part,
      id,
      video: part.video && typeof part.video === 'object' ? { ...part.video } : part.video ?? null,
      _file: null,
      _videoRemove: false
    };
  }
  return { ...part, id };
}

/**
 * Opens a create-mode draft the same way as the VDO extranet duplicate action:
 * copied fields, "(duplicate)" appended to the title, images/videos kept, documents cleared.
 */
export function toDuplicateEditorPayload(
  detail: ContentEditorApiPost | ContentEditorApiResponse | ContentEditorApiPost[] | null | undefined
): ContentEditorPayload | null {
  const mapped = mapContentDetailToEditorPayload(detail);
  if (!mapped) return null;

  const parts = mapped.parts.map(clonePartForDuplicate);
  parts.forEach((part, index) => {
    part.weight = index;
  });

  return {
    ...mapped,
    mode: 'create',
    contentId: null,
    title: `${mapped.title}${DUPLICATE_TITLE_SUFFIX}`,
    parts
  };
}
